import TitrePage from "../components/charte/TitrePage";
import React, { useEffect, useMemo, useState } from "react";
import {
  Box, MenuItem, Tab, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Tabs, TextField, Typography,
} from "@mui/material";
import { AIDE_ARGENT, AIDE_FACTURATION } from "../aide/textesAide";
import BoutonAide from "../components/charte/BoutonAide";
import { BoutonSecondaire } from "../components/charte/Boutons";
import { getDocumentsEmis, getEntitesFacturation } from "../utilities/adminservice";
import { useDroitPaiement } from "../utilities/droitPaiement";
import { messageServeur } from "../utilities/userService";
import {
  DocumentEmis, EtatMission, JournalExport, LIBELLE_NATURE, LIBELLE_TYPE, etatsMissions, jour, telechargerExport,
  telechargerPdf, xof,
} from "../utilities/facturationService";
import { Link } from "react-router-dom";
import { Pastille, PastilleEtat } from "./facturation/composantsFacturation";
import { statutPaiement } from "../utilities/statutsQuete";

/* Argent › Ventes et trésorerie : les documents émis (factures, avoirs,
   devis, contrats) et, pour chaque quête facturée, l'état que calcule
   admin-service : contrat, facturé, reste à facturer, encaissé, reste dû.
   Plus aucune estimation tirée des quêtes. Les exports sont les CSV du
   serveur, enregistrés tels quels. */

const C = { bg: "var(--c-bg)", card: "var(--c-surface)", border: "var(--c-border)", text: "var(--c-text-primary)", sub: "var(--c-text-secondary)" };

const debutDuMois = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-01`; };
const finDuMois = () => { const d = new Date(); const f = new Date(d.getFullYear(), d.getMonth() + 1, 0); return `${f.getFullYear()}-${String(f.getMonth() + 1).padStart(2, "0")}-${String(f.getDate()).padStart(2, "0")}`; };

const EXPORTS: { journal: JournalExport; libelle: string }[] = [
  { journal: "ventes", libelle: "Journal des ventes" },
  { journal: "encaissements", libelle: "Journal des encaissements" },
  { journal: "lignes", libelle: "Détail des lignes" },
];

function Chiffre({ libelle, valeur }: { libelle: string; valeur: number }) {
  return (
    <Box sx={{ border: `1.5px solid ${C.text}`, borderRadius: "8px", background: C.card, px: 2, py: 1.5, flex: 1, minWidth: 170 }}>
      <Typography sx={{ fontSize: 12, color: C.sub }}>{libelle}</Typography>
      <Typography sx={{ fontSize: 20, fontWeight: 700, fontFamily: "var(--font-display)" }}>{xof(valeur)}</Typography>
    </Box>
  );
}

export default function Finance() {
  const droitPaiement = useDroitPaiement();
  const [tab, setTab] = useState(0);
  const [entites, setEntites] = useState<{ pays: string; raisonSociale: string }[]>([]);
  const [pays, setPays] = useState("SN");
  const [du, setDu] = useState(debutDuMois());
  const [au, setAu] = useState(finDuMois());
  const [tous, setTous] = useState<DocumentEmis[]>([]);
  const [etats, setEtats] = useState<Record<string, EtatMission>>({});
  const [erreur, setErreur] = useState<string | null>(null);
  const [chargement, setChargement] = useState(true);

  useEffect(() => {
    getEntitesFacturation().then((l: any[]) => setEntites(l.map((x) => ({ pays: x.entite.pays, raisonSociale: x.entite.raisonSociale }))));
    getDocumentsEmis()
      .then((d: DocumentEmis[]) => setTous(d))
      .catch((e) => setErreur(messageServeur(e, "Documents illisibles.")))
      .finally(() => setChargement(false));
  }, []);

  // Documents du modèle actuel, de l'entité, émis dans la période (dates de Dakar, bornes incluses).
  const docs = useMemo(() => tous.filter((d) => (d.versionModele ?? 1) >= 2 && d.pays === pays
    && d.emisLe.slice(0, 10) >= du && d.emisLe.slice(0, 10) <= au), [tous, pays, du, au]);
  const questIds = useMemo(() => Array.from(new Set(docs.map((d) => d.questId))), [docs]);

  useEffect(() => {
    etatsMissions(questIds).then(setEtats).catch((e) => setErreur(messageServeur(e, "État des quêtes illisible.")));
  }, [questIds]);

  const factures = docs.filter((d) => d.type === "FACTURE");
  const avoirs = docs.filter((d) => d.type === "AVOIR");
  const totalFacture = factures.reduce((s, d) => s + d.montantTtc, 0) - avoirs.reduce((s, d) => s + d.montantTtc, 0);
  const totalEncaisse = factures.reduce((s, d) => s + (d.montantEncaisse ?? 0), 0);
  const totalResteDu = factures.reduce((s, d) => s + (d.resteDuTtc ?? 0), 0);
  const totalAFacturer = questIds.reduce((s, q) => s + (etats[q]?.resteAFacturerTtc ?? 0), 0);

  const exporter = async (journal: JournalExport) => {
    setErreur(null);
    try { await telechargerExport(journal, pays, du, au); }
    catch (e: any) { setErreur(messageServeur(e, "Export refusé.")); }
  };

  return (
    <Box sx={{ minHeight: "100vh", background: C.bg, p: 3 }}>
      <Box sx={{ mb: 3 }}>
        <TitrePage domaine="argent" aide={AIDE_ARGENT.finance}>Ventes et trésorerie</TitrePage>
        <Typography sx={{ color: C.sub, fontSize: 13 }}>
          Documents émis par entité et par période, et état de facturation de chaque quête.
        </Typography>
      </Box>

      {/* Filtres */}
      <Box sx={{ display: "flex", gap: 1.5, alignItems: "center", flexWrap: "wrap", mb: 2 }}>
        <TextField select size="small" label="Entité émettrice" value={pays} onChange={(e) => setPays(e.target.value)} sx={{ minWidth: 220 }}>
          {(entites.length ? entites : [{ pays: "SN", raisonSociale: "" }, { pays: "CI", raisonSociale: "" }]).map((e) => (
            <MenuItem key={e.pays} value={e.pays}>{e.pays}{e.raisonSociale ? ` — ${e.raisonSociale}` : ""}</MenuItem>
          ))}
        </TextField>
        <TextField size="small" type="date" label="Du" value={du} InputLabelProps={{ shrink: true }} onChange={(e) => setDu(e.target.value)} />
        <TextField size="small" type="date" label="Au" value={au} InputLabelProps={{ shrink: true }} onChange={(e) => setAu(e.target.value)} />
        <BoutonAide texte={AIDE_FACTURATION.ventesFiltres} />
      </Box>

      <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap", mb: 2 }}>
        <Chiffre libelle="Facturé net (factures − avoirs)" valeur={totalFacture} />
        <Chiffre libelle="Encaissé sur ces factures" valeur={totalEncaisse} />
        <Chiffre libelle="Reste dû" valeur={totalResteDu} />
        <Chiffre libelle="Reste à facturer (quêtes de la période)" valeur={totalAFacturer} />
      </Box>

      {/* Exports */}
      <Box sx={{ display: "flex", gap: 1, alignItems: "center", flexWrap: "wrap", mb: 2 }}>
        {droitPaiement ? EXPORTS.map((x) => (
          <BoutonSecondaire key={x.journal} size="small" onClick={() => exporter(x.journal)}>{x.libelle} (CSV)</BoutonSecondaire>
        )) : <Typography sx={{ fontSize: 12.5, color: C.sub }}>Exports réservés aux admins qui ont le droit de paiement.</Typography>}
        <BoutonAide texte={AIDE_FACTURATION.exports} />
      </Box>
      {erreur && <Typography sx={{ color: "var(--c-error)", fontSize: 13, mb: 2 }}>{erreur}</Typography>}
      {tous.length >= 200 && (
        <Typography sx={{ color: C.sub, fontSize: 12.5, mb: 1.5 }}>
          Affichage limité aux 200 derniers documents émis ; les exports couvrent toute la période.
        </Typography>
      )}

      <Box sx={{ background: C.card, border: "1.5px solid #1F1E1B", borderRadius: "8px", overflow: "hidden" }}>
        <Tabs value={tab} onChange={(_e, v) => setTab(v)} sx={{ borderBottom: `1px solid ${C.border}`, px: 2 }}>
          <Tab label="État par quête" sx={{ textTransform: "none", fontWeight: 600 }} />
          <Tab label="Documents émis" sx={{ textTransform: "none", fontWeight: 600 }} />
        </Tabs>
        <Box sx={{ p: 2 }}>
          {chargement ? <Typography sx={{ fontSize: 13 }}>Lecture…</Typography> : tab === 0 ? (
            <>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mb: 1 }}>
                <Typography sx={{ fontSize: 13, fontWeight: 600 }}>Quêtes facturées dans la période</Typography>
                <BoutonAide texte={AIDE_FACTURATION.etatParQuete} />
              </Box>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>{["Quête", "Contrat", "Facturé", "Reste à facturer", "Encaissé", "Reste dû", "Statut"].map((h) => <TableCell key={h}>{h}</TableCell>)}</TableRow>
                  </TableHead>
                  <TableBody>
                    {questIds.length === 0 && <TableRow><TableCell colSpan={7} sx={{ color: C.sub }}>Aucun document dans la période.</TableCell></TableRow>}
                    {questIds.map((q) => {
                      const e = etats[q];
                      const titre = docs.find((d) => d.questId === q)?.titreMission ?? q;
                      const encaisse = (e?.factures ?? []).reduce((s, f) => s + (f.montantEncaisse ?? 0), 0);
                      const st = statutPaiement(e?.statutPaiement ?? null, "Non facturée");
                      return (
                        <TableRow key={q}>
                          <TableCell><Link to={`/quetes/${q}`} style={{ color: "inherit" }}>{titre}</Link></TableCell>
                          <TableCell>{xof(e?.montantContratTtc)}</TableCell>
                          <TableCell>{xof(e?.montantFactureTtc)}</TableCell>
                          <TableCell>{xof(e?.resteAFacturerTtc)}</TableCell>
                          <TableCell>{xof(encaisse)}</TableCell>
                          <TableCell>{xof(e?.resteDuTtc)}</TableCell>
                          <TableCell><Pastille libelle={st.libelle} ton={st.ton} /></TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>{["Numéro", "Type", "Émis le", "Client", "Quête", "TTC", "Paiement", "Reste dû", ""].map((h) => <TableCell key={h}>{h}</TableCell>)}</TableRow>
                </TableHead>
                <TableBody>
                  {docs.length === 0 && <TableRow><TableCell colSpan={9} sx={{ color: C.sub }}>Aucun document dans la période.</TableCell></TableRow>}
                  {docs.map((d) => (
                    <TableRow key={d.id}>
                      <TableCell><b>{d.numero}</b></TableCell>
                      <TableCell>{LIBELLE_TYPE[d.type]}{d.natureFacture ? ` · ${LIBELLE_NATURE[d.natureFacture] ?? d.natureFacture}` : ""}{d.documentOrigineNumero ? ` sur ${d.documentOrigineNumero}` : ""}</TableCell>
                      <TableCell>{jour(d.emisLe)}</TableCell>
                      <TableCell>{d.clientRaisonSociale}</TableCell>
                      <TableCell>{d.titreMission}</TableCell>
                      <TableCell>{d.type === "AVOIR" ? `−${xof(d.montantTtc)}` : xof(d.montantTtc)}</TableCell>
                      <TableCell><PastilleEtat etat={d.etatPaiement} /></TableCell>
                      <TableCell>{d.type === "FACTURE" ? xof(d.resteDuTtc) : ""}</TableCell>
                      <TableCell align="right">
                        <BoutonSecondaire size="small" sx={{ py: 0.2 }}
                          onClick={() => telechargerPdf(d.id, d.numero).catch((e) => setErreur(messageServeur(e, "PDF indisponible.")))}>PDF</BoutonSecondaire>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Box>
      </Box>
    </Box>
  );
}
